<script lang="ts">
  import { worldStore, dispatch } from '@sim/store';
  import { clockString, dayOf, isNight, districtMeta } from '@sim/config';
  import { activeBuilding } from '@sim/world';
  import { money, num, heatBand, defenseSummary } from '@sim/selectors';
  import { musicOn } from '@ui-sim/music';

  export let onCity: () => void = () => {};

  $: city = $worldStore;
  $: b = activeBuilding(city);
  $: dist = b ? districtMeta(b.district) : null;
  $: band = heatBand(b ? b.heat : 0);
  $: heatColor =
    band === 'cool' ? 'var(--heat-cool)' : band === 'warm' ? 'var(--heat-warm)' : band === 'hot' ? 'var(--heat-hot)' : 'var(--heat-crit)';
  $: def = b ? defenseSummary(b, city.t) : { defense: 0, safe: 0 };

  const speeds = [
    { v: 0, icon: '⏸' },
    { v: 1, icon: '▶' },
    { v: 2, icon: '⏩' },
    { v: 3, icon: '⏭' },
  ];
</script>

<header>
  <div class="row1">
    <div class="cashcol">
      <div class="cash mono">{money(city.cash)}</div>
      <button class="bldg" on:click={onCity} title="City map">
        {#if b}🏢 {b.name} · {b.district}{#if dist}<span class="demand mono" title={dist.blurb}> · 💲×{dist.demand.toFixed(2)}</span>{/if}{#if city.rep > 0}<span class="rep mono"> · ⭐×{(1 + city.rep).toFixed(1)}</span>{/if} ▾{:else}🏙️ Pick a building ▾{/if}
      </button>
    </div>
    <div class="clock">
      <span class="day">Day {dayOf(city.t)}</span>
      <span class="time mono">{isNight(city.t) ? '🌙' : '☀️'} {clockString(city.t)}</span>
    </div>
    <div class="speeds">
      {#each speeds as s}
        <button class="sp" class:on={city.speed === s.v} on:click={() => dispatch({ type: 'setSpeed', speed: s.v })}>{s.icon}</button>
      {/each}
      <button class="sp" class:on={$musicOn} on:click={() => musicOn.update((v) => !v)} title={$musicOn ? 'Music on' : 'Music off'}>
        {$musicOn ? '🔊' : '🔇'}
      </button>
    </div>
  </div>

  {#if b}
    <div class="res mono">
      <span title="product in stock">📦 {num(b.res.product)}</span>
      <span title="meals">🍽️ {num(b.res.meals)}</span>
      <span title="crew">👥 {b.colonists.length}/{b.maxCrew}</span>
      <span class="def" title="raid defense">🛡️ {def.defense}</span>
    </div>

    <div class="heatrow">
      <div class="heat">
        <div class="htop">
          <span>🔥 HEAT · {b.name}</span>
          <span class="hval mono" style="color:{heatColor}">{b.heat.toFixed(0)}</span>
        </div>
        <div class="htrack">
          <div class="hfill" style="width:{b.heat}%; background:{heatColor}; box-shadow:0 0 10px {heatColor}"></div>
          <div class="hsafe" style="left:{def.safe}%"></div>
        </div>
      </div>
      <button class="laylow" class:on={b.layingLow} on:click={() => dispatch({ type: 'toggleLayLow' })}>
        {b.layingLow ? '❄️ Lying Low' : '🛑 Lay Low'}
      </button>
    </div>
  {:else}
    <div class="pick">Tap “Pick a building” to claim your first plot.</div>
  {/if}
</header>

<style>
  header {
    padding: 8px 10px 6px;
    background: linear-gradient(180deg, #16161f, #12121a);
    border-bottom: 1px solid var(--line);
  }
  .row1 {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .cashcol {
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    align-items: flex-start;
  }
  .cash {
    font-size: 20px;
    font-weight: 800;
    color: var(--cash);
    text-shadow: 0 0 12px color-mix(in srgb, var(--cash) 35%, transparent);
  }
  .bldg {
    font-size: 10px;
    color: var(--ink-dim);
    font-weight: 700;
    background: none;
    border: none;
    padding: 1px 0;
    cursor: pointer;
    text-align: left;
  }
  .bldg:hover {
    color: var(--ink);
  }
  .rep {
    color: var(--heat-warm);
  }
  .demand {
    color: var(--cash);
  }
  .clock {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.15;
  }
  .day {
    font-size: 11px;
    color: var(--ink-dim);
    font-weight: 700;
  }
  .time {
    font-size: 12px;
    color: var(--ink);
  }
  .speeds {
    display: flex;
    gap: 3px;
  }
  .sp {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    font-size: 12px;
    color: var(--ink-dim);
  }
  .sp.on {
    border-color: var(--good);
    color: var(--good);
    background: color-mix(in srgb, var(--good) 16%, var(--panel-2));
  }
  .res {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: var(--ink-dim);
    margin: 6px 0 7px;
    flex-wrap: wrap;
  }
  .res .def {
    margin-left: auto;
    color: var(--heat-cool);
  }
  .pick {
    font-size: 12px;
    color: var(--ink-dim);
    margin: 7px 0 4px;
  }
  .heatrow {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }
  .heat {
    flex: 1;
  }
  .htop {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: var(--ink-dim);
    font-weight: 700;
    margin-bottom: 2px;
  }
  .hval {
    font-size: 13px;
    font-weight: 800;
  }
  .htrack {
    position: relative;
    height: 8px;
    border-radius: 5px;
    background: #000;
    border: 1px solid var(--line);
    overflow: hidden;
  }
  .hfill {
    height: 100%;
    transition: width 0.2s linear;
  }
  .hsafe {
    position: absolute;
    top: -1px;
    bottom: -1px;
    width: 2px;
    background: rgba(255, 255, 255, 0.5);
  }
  .laylow {
    padding: 7px 10px;
    border-radius: 8px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink);
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }
  .laylow.on {
    border-color: var(--heat-cool);
    color: var(--heat-cool);
    background: color-mix(in srgb, var(--heat-cool) 16%, var(--panel-2));
  }
</style>
